/**
 * Operator Sync — Canonical shared contracts.
 *
 * Single source of truth for the bidirectional operator synchronization
 * between Portal (cloud) and Edge (cofre). ADR-035.
 *
 * @module @smartsafe/shared/types/operator-sync
 */

import { z } from 'zod';
import type { SafeEventEnvelope } from './events.js';

// ─── Capacidades Primitivas do Cofre (PA-1 — CONFIRMADO, ADR-035 Aceito) ─────
// Contrato único edge↔nuvem. O edge só aplica primitivas que conhece;
// primitivas fora deste conjunto são descartadas com log.
// Mudança futura no conjunto exige ajuste aqui e propagação.

export const SAFE_CAPABILITIES = [
  'DEPOSIT',
  'COLLECTION',
  'QUERY',
  'EQUIPMENT_ADMIN',
  'DIAGNOSTICS',
  'CONNECTIVITY',
] as const;
export const SafeCapabilitySchema = z.enum(SAFE_CAPABILITIES);
export type SafeCapability = z.infer<typeof SafeCapabilitySchema>;

// ─── Perfis Base canônicos ───────────────────────────────────────────────────
// Identificadores canônicos estáveis sincronizados ao cofre como `role`.
// OPERATOR → 'SAFE_OPERATOR' (compat. edge); ADMIN → 'SAFE_ADMIN'.

export const OPERATOR_BASE_PROFILES = ['SAFE_OPERATOR', 'SAFE_ADMIN', 'SAFE_TECHNICIAN'] as const;
export const OperatorBaseProfileSchema = z.enum(OPERATOR_BASE_PROFILES);
export type OperatorBaseProfileId = z.infer<typeof OperatorBaseProfileSchema>;

/** Rótulos de exibição — desacoplados do identificador canônico. */
export const BASE_PROFILE_LABELS: Record<OperatorBaseProfileId, string> = {
  SAFE_OPERATOR: 'Operador',
  SAFE_ADMIN: 'Administrador do Cofre',
  SAFE_TECHNICIAN: 'Técnico',
};

/**
 * Presets default de capacidades por Perfil Base (PA-1 — CONFIRMADO, ADR-035 Aceito).
 * Mudança futura no conjunto exige ajuste aqui e propagação.
 */
export const BASE_PROFILE_CAPABILITY_PRESETS: Record<OperatorBaseProfileId, SafeCapability[]> = {
  SAFE_OPERATOR: ['DEPOSIT', 'COLLECTION', 'QUERY'],
  SAFE_ADMIN: ['DEPOSIT', 'COLLECTION', 'QUERY', 'EQUIPMENT_ADMIN'],
  SAFE_TECHNICIAN: ['DIAGNOSTICS', 'CONNECTIVITY'],
};

// ─── Origem do cadastro ──────────────────────────────────────────────────────

export const OPERATOR_ORIGINS = ['COFRE', 'PORTAL'] as const;
export const OperatorOriginSchema = z.enum(OPERATOR_ORIGINS);
export type OperatorOrigin = z.infer<typeof OperatorOriginSchema>;

// ─── Perfil efetivo (base ou customizado que herda de um base) ───────────────

export interface OperatorProfile {
  profileId: string;
  base: OperatorBaseProfileId;
  isCustom: boolean;
  tenantId: string | null;
  capabilityIds: string[];
}

// ─── Identidade do operador para sincronização ──────────────────────────────

export interface OperatorSyncIdentity {
  operatorId: string;
  name: string;
  chapa: string;
  monitoringCode: string | null;
  role: OperatorBaseProfileId;
  active: boolean;
  blocked?: boolean;
}

// ─── Contratos de evento (Portal → Producer) ────────────────────────────────

export type OperatorSyncOp = 'UPSERT' | 'DEACTIVATE';

export interface OperatorSyncPayload {
  intentId: string;
  op: OperatorSyncOp;
  operator: OperatorSyncIdentity;
  pinPresent: boolean;
  pinHash?: string;
  grantsByDevice: Array<{ deviceId: string; capabilityIds: string[] }>;
}

/**
 * @deprecated Não use como tipo do `detail` do evento `OperatorSyncRequested`.
 * O evento é operator-scoped (abrange 0..N cofres via `payload.grantsByDevice`) e
 * NÃO é um `SafeEventEnvelope` device-scoped. Use `OperatorSyncRequestedDetail`.
 * Mantido por retrocompatibilidade (ADR-040).
 */
export type OperatorSyncEvent = SafeEventEnvelope<OperatorSyncPayload>;

/**
 * Wire contract canônico do evento `OperatorSyncRequested` no EventBridge (ADR-040).
 *
 * Formato lean operator-scoped realmente emitido pelo Portal (BFF `operator-sync-emitter`)
 * e consumido pelo Lambda `operator-sync-producer`. `correlationId` fica no nível
 * superior do detail; não há `metadata` nem campos device-scoped
 * (`deviceId`/`safeId`/`nsu`/`designadorId`/`localId`/`source`/`metadata.hash`).
 */
export interface OperatorSyncRequestedDetail {
  eventId: string;
  eventType: 'OperatorSyncRequested';
  version: '1.0';
  timestamp: string;
  tenantId: string;
  correlationId: string;
  payload: OperatorSyncPayload;
}

// ─── Contratos de evento (Cofre → Portal — Reconciliação) ───────────────────

export interface LocalOperatorReportedPayload {
  intentId: string;
  operatorId: string;
  name: string;
  chapa: string;
  role: OperatorBaseProfileId;
  monitoringCode: string | null;
  pinHash?: string;
}

export type LocalOperatorReportedEvent = SafeEventEnvelope<LocalOperatorReportedPayload>;

// ─── Registro do desired.operators (shadow) ─────────────────────────────────

export interface DesiredOperatorRecord {
  operator_id: string;
  name: string;
  chapa: string;
  role: OperatorBaseProfileId;
  monitoring_code: string | null;
  capabilities: SafeCapability[];
  active: boolean;
  pin_hash?: string;
  blocked?: boolean;
}

// ─── Resolução de perfil efetivo ────────────────────────────────────────────

/** Mapeamento de legado 'OPERATOR' para o identificador canônico estável. */
const LEGACY_PROFILE_MAP: Record<string, OperatorBaseProfileId> = {
  OPERATOR: 'SAFE_OPERATOR',
};

/**
 * Resolve o perfil efetivo para o identificador canônico do Perfil Base.
 *
 * - Perfil base retorna ele mesmo.
 * - Perfil customizado retorna o base do qual herda.
 * - Legacy 'OPERATOR' → 'SAFE_OPERATOR'.
 * - Perfil desconhecido lança erro.
 */
export function resolveEffectiveBaseProfile(profile: OperatorProfile): OperatorBaseProfileId {
  return profile.base;
}

/**
 * Resolve um profileId (string) para o OperatorBaseProfileId canônico.
 *
 * - Perfil base retorna ele mesmo.
 * - Legacy 'OPERATOR' → 'SAFE_OPERATOR'.
 * - Perfil customizado: busca no conjunto de perfis do tenant.
 *
 * @throws Error se o perfil não pertence ao conjunto válido.
 */
export function resolveProfileId(
  profileId: string,
  tenantProfiles: ReadonlyArray<OperatorProfile>,
): OperatorBaseProfileId {
  const asBase = profileId as OperatorBaseProfileId;
  if (OPERATOR_BASE_PROFILES.includes(asBase)) {
    return asBase;
  }
  const legacy = LEGACY_PROFILE_MAP[profileId];
  if (legacy) {
    return legacy;
  }
  const found = tenantProfiles.find((p) => p.profileId === profileId);
  if (found) {
    return found.base;
  }
  throw new Error(`Perfil inválido: ${profileId}`);
}

/**
 * Valida que o profileId pertence ao conjunto válido do tenant
 * (perfis base ∪ perfis customizados do tenant).
 */
export function isValidProfileForTenant(
  profileId: string,
  tenantProfiles: ReadonlyArray<OperatorProfile>,
): boolean {
  const asBase = profileId as OperatorBaseProfileId;
  if (OPERATOR_BASE_PROFILES.includes(asBase)) {
    return true;
  }
  if (LEGACY_PROFILE_MAP[profileId]) {
    return true;
  }
  return tenantProfiles.some((p) => p.profileId === profileId);
}

// ─── Resolução de capacidades primitivas ────────────────────────────────────

/** Entrada do catálogo de capacidades (mapeamento opcional → primitiva). */
export interface CatalogCapability {
  capabilityId: string;
  primitive: SafeCapability | null;
}

/**
 * Resolve ids de capacidades concedidas → subconjunto de SAFE_CAPABILITIES.
 *
 * Aceita dupla convenção (ADR-035, emenda S166):
 * 1. **Primitiva direta (pass-through)**: se o id já é uma primitiva conhecida
 *    (`∈ SAFE_CAPABILITIES`), é incluído diretamente no resultado sem lookup.
 * 2. **Id de catálogo (UUID)**: resolvido via `catalog` — se existir entrada com
 *    `primitive` válido em SAFE_CAPABILITIES, a primitiva mapeada é incluída.
 *
 * Retorna subconjunto deduplicado de SAFE_CAPABILITIES. Ids que não se
 * encaixam em nenhuma das duas convenções são descartados silenciosamente.
 */
export function resolvePrimitives(
  catalog: ReadonlyArray<CatalogCapability>,
  grantedCapabilityIds: ReadonlyArray<string>,
): SafeCapability[] {
  const result = new Set<SafeCapability>();
  for (const id of grantedCapabilityIds) {
    // Pass-through: id que já é primitiva conhecida entra direto (ADR-035, emenda S166).
    if ((SAFE_CAPABILITIES as readonly string[]).includes(id)) {
      result.add(id as SafeCapability);
      continue;
    }
    const entry = catalog.find((c) => c.capabilityId === id);
    if (!entry || entry.primitive === null) {
      continue;
    }
    if (SAFE_CAPABILITIES.includes(entry.primitive)) {
      result.add(entry.primitive);
    }
  }
  return [...result];
}

// ─── Validadores canônicos compartilhados ───────────────────────────────────

const PIN_REGEX = /^[a-zA-Z0-9]{4,14}$/;
const INTENT_ID_MAX_LENGTH = 128;

/** Valida PIN: 4 a 14 caracteres alfanuméricos (a-z, A-Z, 0-9). PINs numéricos permanecem válidos como subconjunto. */
export function isValidPin(pin: string): boolean {
  return PIN_REGEX.test(pin);
}

/** Valida intentId: string de 1 a 128 caracteres. */
export function isValidIntentId(id: string): boolean {
  return id.length >= 1 && id.length <= INTENT_ID_MAX_LENGTH;
}

/** Valida se role é um OperatorBaseProfileId válido. */
export function isValidBaseProfile(role: string): boolean {
  return (OPERATOR_BASE_PROFILES as readonly string[]).includes(role);
}

// ─── Construção do registro desired.operators ───────────────────────────────

/**
 * Produz o registro do desired.operators para publicação no Device Shadow.
 *
 * Inclui operator_id, name, chapa, role, monitoring_code, capabilities, active.
 * pin_hash opcional — quando ausente, edge preserva o hash existente.
 */
export function buildDesiredOperatorRecord(
  identity: OperatorSyncIdentity,
  capabilities: SafeCapability[],
  pinHash?: string,
): DesiredOperatorRecord {
  const record: DesiredOperatorRecord = {
    operator_id: identity.operatorId,
    name: identity.name,
    chapa: identity.chapa,
    role: identity.role,
    monitoring_code: identity.monitoringCode,
    capabilities,
    active: identity.active,
  };
  if (pinHash) {
    record.pin_hash = pinHash;
  }
  if (identity.blocked !== undefined) {
    record.blocked = identity.blocked;
  }
  return record;
}

// ─── Capability Restriction (Perfil-based) ───────────────────────────────────

const CASH_CAPABILITIES: SafeCapability[] = ['DEPOSIT', 'COLLECTION', 'QUERY', 'EQUIPMENT_ADMIN'];
const TECH_CAPABILITIES: SafeCapability[] = ['DIAGNOSTICS', 'CONNECTIVITY'];

/**
 * Returns the set of capabilities that CAN be granted to a given profile.
 * Used by both UI (checkbox enable/disable) and BFF (bulk filtering).
 */
export function getAllowedCapabilities(profile: OperatorBaseProfileId): SafeCapability[] {
  switch (profile) {
    case 'SAFE_TECHNICIAN':
      return [...TECH_CAPABILITIES];
    case 'SAFE_OPERATOR':
      return ['DEPOSIT', 'COLLECTION', 'QUERY'];
    case 'SAFE_ADMIN':
      return [...CASH_CAPABILITIES];
  }
}

/**
 * Returns the set of capabilities that are BLOCKED for a given profile.
 * Complement of getAllowedCapabilities within SAFE_CAPABILITIES.
 */
export function getRestrictedCapabilities(profile: OperatorBaseProfileId): SafeCapability[] {
  const allowed = new Set(getAllowedCapabilities(profile));
  return SAFE_CAPABILITIES.filter((c) => !allowed.has(c));
}

/**
 * Filters a set of requested capabilities, removing any that are restricted
 * for the given profile. Used in BFF bulk operations.
 */
export function filterCapabilitiesByProfile(
  profile: OperatorBaseProfileId,
  requested: SafeCapability[],
): SafeCapability[] {
  const allowed = new Set(getAllowedCapabilities(profile));
  return requested.filter((c) => allowed.has(c));
}

// ─── Mascaramento de PIN em logs/auditoria ──────────────────────────────────

const PIN_FIELD_NAMES = new Set(['pin', 'pinHash', 'pin_hash', 'pinEquipamento']);
const MASKED_VALUE = '[MASKED]';

/**
 * Deep-replace qualquer campo nomeado pin, pinHash, pin_hash, pinEquipamento
 * com '[MASKED]'. Retorna uma cópia profunda — não altera o original.
 */
export function maskPinFields<T>(obj: T): T {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map((item: unknown) => maskPinFields(item)) as unknown as T;
  }
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    if (PIN_FIELD_NAMES.has(key) && typeof value === 'string') {
      result[key] = MASKED_VALUE;
    } else if (typeof value === 'object' && value !== null) {
      result[key] = maskPinFields(value);
    } else {
      result[key] = value;
    }
  }
  return result as T;
}
